import { useEffect } from 'react';
import Widget from 'components/Dashboard/Widget';
import useTranslations from 'contexts/basicData/useTranslations';
import { useKPIGetReturnToLogisticsTime } from 'api/kpi/kpi';
import { KPIReturnToLogisticsRow } from 'api/model';
import { KPIsWidgetEnum, LongTitles } from '../../utils';
import useOperationsKpiFilter from './useOperationsKpiFilter';
import {
  ControlFigures,
  hrs,
  nf,
  tableStyle,
  tdNumStyle,
  tdStyle,
  thNumStyle,
  thStyle,
  totalRowStyle,
  WidgetState,
} from '../management/shared';

const Cells = ({ row }: { row: KPIReturnToLogisticsRow }) => (
  <>
    <td style={tdNumStyle}>{nf(row.cars)}</td>
    <td style={tdNumStyle}>{hrs(row.medianHours)}</td>
    <td style={tdNumStyle}>{hrs(row.averageHours)}</td>
  </>
);

/**
 * Inköp och inbyten: tid från "På väg tillbaka" hos kunden till framme i Logistik Strängnäs, för
 * bilar som FT körde hela vägen. Se KPILogic.GetReturnToLogisticsTime.
 */
const ReturnToLogisticsWidget = () => {
  const { isReady, isDateRangeTooLong, filter, deps } =
    useOperationsKpiFilter();
  const getData = useKPIGetReturnToLogisticsTime();
  const { caseTypes } = useTranslations();

  useEffect(() => {
    if (isReady) {
      getData.mutate({ data: filter });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const result = getData.data;

  return (
    <Widget
      title={LongTitles[KPIsWidgetEnum.ReturnToLogistics]}
      count={result?.cars}
      isLoading={getData.isLoading}
      info={
        'Inköp, auktion och inbyte. Från att fälttestaren satt "På väg tillbaka" på kundbenet (kör iväg från kunden med bilen) till GPS-ankomsten i Logistik Strängnäs — eller benets sluttid när GPS-ankomst saknas. ' +
        'Bilar som någon gång under resan gått på lastbil räknas inte. Mellanlandningar räknas med (klocktid, inte körtid). Perioden gäller ankomsten i Strängnäs, länet är kundens (kundbenets från-län).'
      }
    >
      <WidgetState
        isLoading={getData.isLoading}
        isDateRangeTooLong={isDateRangeTooLong}
        isEmpty={
          !result || (result.cars === 0 && result.strangnasFacilityResolved)
        }
      >
        {result && !result.strangnasFacilityResolved ? (
          <p>
            Logistik Strängnäs hittades inte bland företagen — måttet går inte
            att räkna.
          </p>
        ) : (
          <>
            <p style={{ fontSize: '15px', margin: '4px 0 12px' }}>
              Median <strong>{hrs(result?.medianHours)}</strong> · snitt{' '}
              {hrs(result?.averageHours)} · {nf(result?.cars ?? 0)} bilar
            </p>

            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Ärendetyp</th>
                  <th style={thNumStyle}>Bilar</th>
                  <th style={thNumStyle}>Median</th>
                  <th style={thNumStyle}>Snitt</th>
                </tr>
              </thead>
              <tbody>
                {(result?.byCaseType ?? []).map((row) => (
                  <tr
                    key={row.caseType ?? 'none'}
                    style={{ borderBottom: '1px solid #eee' }}
                  >
                    <td style={tdStyle}>
                      {row.caseType != null ? caseTypes[row.caseType] : '–'}
                    </td>
                    <Cells row={row} />
                  </tr>
                ))}
              </tbody>
            </table>

            <table style={{ ...tableStyle, marginTop: '16px' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Kundens län</th>
                  <th style={thNumStyle}>Bilar</th>
                  <th style={thNumStyle}>Median</th>
                  <th style={thNumStyle}>Snitt</th>
                </tr>
              </thead>
              <tbody>
                {(result?.byCounty ?? []).map((row) => (
                  <tr
                    key={row.countyName}
                    style={{ borderBottom: '1px solid #eee' }}
                  >
                    <td style={tdStyle}>{row.countyName}</td>
                    <Cells row={row} />
                  </tr>
                ))}
                {result && (
                  <tr style={totalRowStyle}>
                    <td style={tdStyle}>Totalt</td>
                    <td style={tdNumStyle}>{nf(result.cars)}</td>
                    <td style={tdNumStyle}>{hrs(result.medianHours)}</td>
                    <td style={tdNumStyle}>{hrs(result.averageHours)}</td>
                  </tr>
                )}
              </tbody>
            </table>

            <ControlFigures
              items={[
                `${nf(result?.excludedTruckCars ?? 0)} bilar som gått på lastbil räknas inte`,
                result?.carsWithoutReturningEvent
                  ? `${nf(result.carsWithoutReturningEvent)} bilar saknar "På väg tillbaka" och räknas inte`
                  : null,
                result?.returningAfterArrival
                  ? `${nf(result.returningAfterArrival)} bilar fick "På väg tillbaka" först efter framkomsten och räknas inte`
                  : null,
                result?.arrivalsFromEndTime
                  ? `ankomsten tagen ur sluttiden för ${nf(result.arrivalsFromEndTime)} bilar (ingen GPS-ankomst)`
                  : null,
              ]}
            />
          </>
        )}
      </WidgetState>
    </Widget>
  );
};

export default ReturnToLogisticsWidget;
